/** Default suggest-pin footprint used when callers omit size. */
const SUGGEST_PIN_WIDTH = 300;
const SUGGEST_PIN_MIN_HEIGHT = 160;
const SUGGEST_PIN_GRID_STRIDE = 324;
const OVERLAP_GAP = 80;
const SELECTION_OFFSET_X = 420;
const MAX_NUDGE_RINGS = 28;

/**
 * Collect page-space rects of existing pin-like shapes to avoid overlaps.
 */
export function collectOccupiedPinRects(editor) {
  if (!editor?.getCurrentPageShapes) return [];
  const rects = [];
  for (const shape of editor.getCurrentPageShapes()) {
    if (
      shape.type !== 'pin-card'
      && shape.type !== 'suggest-pin'
      && shape.type !== 'conversation-pin'
    ) {
      continue;
    }
    const w = shape.props?.w ?? SUGGEST_PIN_WIDTH;
    const h = shape.props?.h ?? SUGGEST_PIN_MIN_HEIGHT;
    rects.push({ x: shape.x, y: shape.y, w, h });
  }
  return rects;
}

export function collectSelectedPinCenters(editor) {
  if (!editor?.getSelectedShapeIds) return [];
  const centers = [];
  for (const shapeId of editor.getSelectedShapeIds()) {
    const shape = editor.getShape(shapeId);
    if (!shape || shape.type !== 'pin-card') continue;
    const w = shape.props?.w ?? 300;
    const h = shape.props?.h ?? 400;
    centers.push({ x: shape.x + w / 2, y: shape.y + h / 2 });
  }
  return centers;
}

/** Centers of all pin-cards — used when nothing is selected. */
export function collectAllPinCenters(editor) {
  if (!editor?.getCurrentPageShapes) return [];
  const centers = [];
  for (const shape of editor.getCurrentPageShapes()) {
    if (shape.type !== 'pin-card') continue;
    const w = shape.props?.w ?? 300;
    const h = shape.props?.h ?? 400;
    centers.push({ x: shape.x + w / 2, y: shape.y + h / 2 });
  }
  return centers;
}

function averageCenters(centers) {
  if (!centers.length) return null;
  const cx = centers.reduce((sum, point) => sum + point.x, 0) / centers.length;
  const cy = centers.reduce((sum, point) => sum + point.y, 0) / centers.length;
  return { x: cx, y: cy };
}

/** True when candidate intersects any occupied rect (with gap padding). */
export function rectOverlapsOccupied(rect, occupied, gap = OVERLAP_GAP) {
  for (const other of occupied) {
    if (
      rect.x < other.x + other.w + gap
      && rect.x + rect.w + gap > other.x
      && rect.y < other.y + other.h + gap
      && rect.y + rect.h + gap > other.y
    ) {
      return true;
    }
  }
  return false;
}

function resolvePinSize(size = {}) {
  const w = Number(size.w);
  const h = Number(size.h);
  return {
    w: Number.isFinite(w) && w > 0 ? w : SUGGEST_PIN_WIDTH,
    h: Number.isFinite(h) && h > 0 ? h : SUGGEST_PIN_MIN_HEIGHT,
  };
}

/**
 * Nudge a candidate top-left so it does not collide with occupied rects.
 * Searches expanding rings until a free slot is found (uses the pin's real size).
 */
export function nudgeAwayFromOccupied(position, occupiedRects = [], index = 0, size = {}) {
  const { w, h } = resolvePinSize(size);
  const strideX = w + OVERLAP_GAP;
  const strideY = h + OVERLAP_GAP;
  const baseX = Number(position?.x) || 0;
  const baseY = Number(position?.y) || 0;

  if (!occupiedRects.length) {
    return { x: baseX, y: baseY };
  }

  for (let ring = 0; ring <= MAX_NUDGE_RINGS; ring += 1) {
    for (let col = -ring; col <= ring; col += 1) {
      for (let row = -ring; row <= ring; row += 1) {
        if (ring > 0 && Math.abs(col) !== ring && Math.abs(row) !== ring) continue;
        const candidate = {
          x: baseX + col * strideX + (index % 3) * 16,
          y: baseY + row * strideY,
          w,
          h,
        };
        if (!rectOverlapsOccupied(candidate, occupiedRects)) {
          return { x: candidate.x, y: candidate.y };
        }
      }
    }
  }

  // Guaranteed free slot: to the right of every occupied pin.
  let maxRight = baseX;
  let avgY = baseY;
  for (const rect of occupiedRects) {
    maxRight = Math.max(maxRight, rect.x + rect.w);
    avgY += rect.y;
  }
  avgY = occupiedRects.length ? avgY / (occupiedRects.length + 1) : baseY;
  return {
    x: maxRight + OVERLAP_GAP + (index % 3) * 16,
    y: avgY + Math.floor(index / 3) * strideY,
  };
}

/**
 * Resolve suggest-pin / fallback coords from AI, selection/context anchors, then grid.
 * Always runs overlap nudging so pins land in open space near related content.
 *
 * @param {{ x?: number, y?: number }} suggestPin
 * @param {number} index
 * @param {{
 *   selectedCenters?: { x: number, y: number }[],
 *   allPinCenters?: { x: number, y: number }[],
 *   occupiedRects?: { x: number, y: number, w: number, h: number }[],
 *   viewportBounds?: { x: number, y: number, w: number, h: number } | null,
 *   pinSize?: { w?: number, h?: number },
 * } | { x: number, y: number }[]} [context]
 */
export function resolveSuggestPinFallbackPosition(suggestPin, index, context = []) {
  // Back-compat: older callers passed selectedCenters as a bare array.
  const options = Array.isArray(context)
    ? { selectedCenters: context }
    : context || {};

  const {
    selectedCenters = [],
    allPinCenters = [],
    occupiedRects = [],
    viewportBounds = null,
    pinSize = {},
  } = options;

  const { w, h } = resolvePinSize(pinSize);
  const stride = Math.max(w, SUGGEST_PIN_WIDTH) + 24;

  let anchorX;
  let anchorY;

  if (Number.isFinite(suggestPin?.x) && Number.isFinite(suggestPin?.y)) {
    anchorX = suggestPin.x;
    anchorY = suggestPin.y;
  } else {
    const selected = averageCenters(selectedCenters);
    const board = averageCenters(allPinCenters);
    if (selected) {
      anchorX = selected.x + SELECTION_OFFSET_X + (index % 3) * 40 - w / 2;
      anchorY = selected.y + index * (stride * 0.35) - h / 2;
    } else if (board) {
      anchorX = board.x + SELECTION_OFFSET_X + (index % 3) * 40 - w / 2;
      anchorY = board.y + index * (stride * 0.35) - h / 2;
    } else if (viewportBounds) {
      anchorX =
        viewportBounds.x
        + Math.max(40, viewportBounds.w * 0.55)
        + (index % 3) * stride;
      anchorY = viewportBounds.y + 80 + Math.floor(index / 3) * stride;
    } else {
      anchorX = 680 + (index % 3) * SUGGEST_PIN_GRID_STRIDE;
      anchorY = 80 + Math.floor(index / 3) * SUGGEST_PIN_GRID_STRIDE;
    }
  }

  return nudgeAwayFromOccupied(
    { x: anchorX, y: anchorY },
    occupiedRects,
    index,
    { w, h }
  );
}
